import type { TradingSigner } from "@src/features/tradingSigner"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import { getDexFromCoin } from "../marketIdentity"
import { cancelOrders } from "../services/hyperliquidExchangeService"
import { getAssetMeta } from "../services/hyperliquidService"
import type { HLOpenOrder } from "../types"
import { invalidateTradeQueries } from "../utils/invalidateTradeQueries"

export function useCancelOpenOrder({
  address,
  tradingSigner,
  getOrderAssetId,
}: {
  address?: `0x${string}`
  tradingSigner: TradingSigner | null
  getOrderAssetId?: (order: HLOpenOrder) => number | undefined
}) {
  const queryClient = useQueryClient()
  const [cancelingOrderIds, setCancelingOrderIds] = useState<
    ReadonlySet<number>
  >(() => new Set())
  const [cancelError, setCancelError] = useState<string | null>(null)

  const cancelOpenOrder = useCallback(
    async (order: HLOpenOrder) => {
      if (!tradingSigner) {
        setCancelError("Connect a trading signer to cancel orders.")
        return
      }

      setCancelError(null)
      setCancelingOrderIds((current) => new Set(current).add(order.oid))

      try {
        const knownOrderAssetId = getOrderAssetId?.(order)
        const orderAssetId =
          knownOrderAssetId ?? (await getOrderAssetIdFromService(order.coin))

        const result = await cancelOrders(tradingSigner, {
          cancels: [
            {
              orderAssetId,
              orderId: order.oid,
            },
          ],
        })

        if (!result.ok) {
          setCancelError(result.error)
          return
        }

        await invalidateTradeQueries(
          queryClient,
          address,
          getDexFromCoin(order.coin)
        )
      } catch (error) {
        setCancelError(error instanceof Error ? error.message : String(error))
      } finally {
        setCancelingOrderIds((current) => {
          const next = new Set(current)
          next.delete(order.oid)
          return next
        })
      }
    },
    [address, getOrderAssetId, queryClient, tradingSigner]
  )

  return { cancelOpenOrder, cancelingOrderIds, cancelError }
}

async function getOrderAssetIdFromService(coin: string) {
  const meta = await getAssetMeta(coin)
  if (!meta.ok) {
    throw new Error(meta.error)
  }
  return meta.data.orderAssetId
}
